import {
  createFileRoute,
  getRouteApi,
  useNavigate,
} from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { CoursePackageImportButton } from '@/admin/content/CoursePackageImportDialog';
import { LANGUAGE_OPTIONS, type Language } from '@/admin/languages';
import { SeriesHeader, TabHeading } from '@/admin/ui/ContentNav';
import { createTablePage } from '@/admin/ui/createTablePage';
import { Select } from '@/admin/ui/Form';
import { LIST_PAGE_SIZE, Pagination } from '@/admin/ui/Pagination';
import { Table } from '@/admin/ui/Table';
import { formatWhen, VersionPill } from '@/admin/ui/Versions';
import { CreateCourseButton } from '@/admin/widgets/CreateCourseButton';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import i18n from '~/i18n';

type CourseAdminSummary = components['schemas']['CourseAdminSummary'];

// Optional key, not an undefined value: a required key would force every
// `<Link to="/admin/courses/">` in the console to pass `search`.
type CoursesSearch = { series?: number; language?: Language };

// The route names this component and the component reads the route's search, so
// the two infer circularly. getRouteApi plus an explicit CoursesSearch breaks it.
const routeApi = getRouteApi('/admin/courses/');

const columnHelper = createColumnHelper<CourseAdminSummary>();

// One row per course: its live version (or its only version), and a draft only when
// one is open, so "is anyone working on this?" shows at a glance.
const columns = [
  columnHelper.accessor('id', { header: () => i18n.t('admin.common.id') }),
  columnHelper.accessor('title', {
    header: () => i18n.t('admin.common.title'),
  }),
  columnHelper.accessor('language', {
    header: () => i18n.t('admin.common.language'),
    // Null for migrated courses that predate series assignment.
    cell: (c) => c.getValue() ?? '—',
  }),
  columnHelper.display({
    id: 'version',
    header: () => i18n.t('admin.courses.col.version'),
    cell: (c) => {
      const row = c.row.original;
      // A never-published course is its own draft.
      if (row.live_version == null)
        return (
          <>
            <VersionPill state="draft" version={row.version} />{' '}
            <Muted>{i18n.t('admin.courses.notYetPublished')}</Muted>
          </>
        );
      return (
        <>
          <VersionPill state="live" version={row.live_version} />
          {row.draft_version != null && (
            <>
              {' '}
              <VersionPill
                state="draft"
                version={row.draft_version}
                detail={
                  row.draft_started_at
                    ? row.draft_started_by?.name
                      ? i18n.t('admin.courses.draftStartedBy', {
                          name: row.draft_started_by.name,
                          when: formatWhen(row.draft_started_at),
                          interpolation: { escapeValue: false },
                        })
                      : i18n.t('admin.courses.draftStarted', {
                          when: formatWhen(row.draft_started_at),
                          interpolation: { escapeValue: false },
                        })
                    : undefined
                }
              />
            </>
          )}
        </>
      );
    },
  }),
  columnHelper.accessor('in_progress', {
    header: () => i18n.t('admin.courses.col.inProgress'),
  }),
];

// Language filter and package import for the all-courses list. A new course
// from a package is placed in a series, so the dialog asks for one.
function CourseActions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { language } = routeApi.useSearch() as CoursesSearch;
  return (
    <Actions>
      <Select
        aria-label={t('admin.courses.languageFilter')}
        value={language ?? ''}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          const next = e.target.value as Language | '';
          void navigate({
            to: '/admin/courses/',
            search: next ? { language: next } : {},
            replace: true,
          });
        }}
      >
        <option value="">{t('admin.series.allLanguages')}</option>
        {LANGUAGE_OPTIONS.map(([code, label]) => (
          <option key={code} value={code}>
            {t(label)}
          </option>
        ))}
      </Select>
      <CoursePackageImportButton
        label={t('admin.content.packages.importButton')}
      />
      <CreateCourseButton />
    </Actions>
  );
}

const AllCourses = createTablePage<
  CourseAdminSummary,
  '/admin/courses/$courseId/$version'
>({
  name: 'admin.nav.courses',
  // Admin endpoint: all statuses (the student list_courses is gated to a fully
  // published path).
  useData: (page: number) => {
    const { language } = routeApi.useSearch() as CoursesSearch;
    return api.adminListCourses.useQuery({
      query: {
        limit: LIST_PAGE_SIZE,
        offset: LIST_PAGE_SIZE * page,
        ...(language ? { language } : {}),
      },
    });
  },
  columns,
  itemBaseUrl: '/admin/courses/$courseId/$version',
  getParams: (row) => ({
    courseId: String(row.id),
    version: String(row.version),
  }),
  actions: <CourseActions />,
});

// `/admin/courses?series=N`: the series' Courses tab, under the series' own header.
function SeriesCourses({ series }: { series: number }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const seriesQuery = api.adminGetSeries.useQuery({
    path: { series_id: series },
  });
  const { data, isLoading, error } = api.adminListCourses.useQuery({
    query: {
      series_id: series,
      limit: LIST_PAGE_SIZE,
      offset: LIST_PAGE_SIZE * page,
    },
  });
  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={setPage}
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      {seriesQuery.data && <SeriesHeader series={seriesQuery.data} />}
      <TabHeading
        action={
          <Actions>
            <CoursePackageImportButton
              target={{ type: 'series', id: series }}
              label={t('admin.content.packages.importButton')}
            />
            <CreateCourseButton />
          </Actions>
        }
      >
        {t('admin.nav.courses')}
      </TabHeading>
      {isLoading ? (
        <p>{t('admin.courses.loading')}</p>
      ) : error ? (
        <p>{t('admin.courses.errorLoading')}</p>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/admin/courses/$courseId/$version/"
            getParams={(row) => ({
              courseId: String(row.id),
              version: String(row.version),
            })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

function CoursesPage() {
  const { series } = routeApi.useSearch() as CoursesSearch;
  const { language } = routeApi.useSearch() as CoursesSearch;
  // Keyed on the filter so the table's page index starts over with it.
  return series !== undefined ? (
    <SeriesCourses series={series} />
  ) : (
    <AllCourses key={language ?? 'all'} />
  );
}

export const Route = createFileRoute('/admin/courses/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (s: Record<string, unknown>): CoursesSearch => {
    const series = Number(s.series);
    const language = LANGUAGE_OPTIONS.find(([code]) => code === s.language);
    return {
      ...(Number.isFinite(series) && series > 0 ? { series } : {}),
      ...(language ? { language: language[0] } : {}),
    };
  },
  component: CoursesPage,
});

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;

  select {
    width: auto;
  }
`;

const Muted = styled.span`
  color: var(--grey-700);
  font-size: 0.8125rem;
`;
